import xhrService from '../xhrService';
import { AssetStatus, AssetType } from './asset';

export interface AssetFieldAnswer {
  id?: number;
  asset_field_type_id: number | null;
  value?: string | null;
}

export interface AssetUpload {
  id: number;
  upload_id: number;
  inspection_id: number | null;
  upload_type?: string;
  upload?: { id: number; name?: string; GUID?: string; mime_type?: string };
  inspection?: {
    id: number;
    created_date: string;
    inspection_template?: { name: string };
  } | null;
}

export interface ProjectAsset {
  id: number;
  project_id: number;
  asset_type_id: number;
  asset_status_id: number;
  project_inspection_templates?: {
    id: number;
    inspection_template?: { name?: string };
  }[];
  asset_name: string;
  notes?: string;
  city?: string;
  state?: string;
  county?: string;
  latitude?: number;
  longitude?: number;
  archived_at?: string | null;
  archived_for_inspection?: boolean;
  previous_inspections?: { same_form: number | null; any_form: number | null };
  asset_type?: AssetType;
  asset_status?: AssetStatus;
  field_answers?: AssetFieldAnswer[];
  inspection_answers?: AssetFieldAnswer[];
  uploads?: AssetUpload[];
  findings?: {
    id: number;
    type?: string;
    number?: number;
    condition?: string;
    initial_inspection_id?: number;
    completed_inspection_id?: number;
    finding_type?: { details?: { color?: string }[] };
  }[];
}

export type ProjectAssetInput = Partial<ProjectAsset> & {
  field_answers?: AssetFieldAnswer[];
  /** Inspection type ids to assign the asset to (sent on create/update). */
  project_inspection_template_ids?: number[];
};

const assetsUrl = (projectId: number, id?: number) =>
  `/api/projects/${projectId}/assets${id ? `/${id}` : ''}`;

export const projectAssetsApi = {
  index: (projectId: number, inspectionId?: number): Promise<ProjectAsset[]> =>
    xhrService
      .get(
        assetsUrl(projectId),
        inspectionId ? { params: { inspection_id: inspectionId } } : undefined,
      )
      .then((r) => r.data),

  show: (
    projectId: number,
    id: number,
    inspectionId?: number,
  ): Promise<ProjectAsset> =>
    xhrService
      .get(
        assetsUrl(projectId, id),
        inspectionId ? { params: { inspection_id: inspectionId } } : undefined,
      )
      .then((r) => r.data),

  create: (projectId: number, data: ProjectAssetInput): Promise<ProjectAsset> =>
    xhrService.post(assetsUrl(projectId), data).then((r) => r.data),

  update: (
    projectId: number,
    id: number,
    data: ProjectAssetInput,
  ): Promise<ProjectAsset> =>
    xhrService.put(assetsUrl(projectId, id), data).then((r) => r.data),

  setStatus: (
    projectId: number,
    id: number,
    data: { asset_status_id: number; inspection_id?: number },
  ): Promise<ProjectAsset> =>
    xhrService
      .put(`${assetsUrl(projectId, id)}/status`, data)
      .then((r) => r.data),

  archive: (projectId: number, id: number) =>
    xhrService.put(`${assetsUrl(projectId, id)}/archive`).then((r) => r.data),

  restore: (projectId: number, id: number) =>
    xhrService.put(`${assetsUrl(projectId, id)}/restore`).then((r) => r.data),

  saveInspectionAnswer: (
    projectId: number,
    id: number,
    data: {
      inspection_id: number;
      asset_field_type_id?: number | null;
      value?: string | null;
    },
  ) =>
    xhrService
      .post(`${assetsUrl(projectId, id)}/inspection-answers`, data)
      .then((r) => r.data),

  lastNote: (
    projectId: number,
    id: number,
    excludeInspectionId?: number,
  ): Promise<{
    inspection_id: number;
    inspection_date: string | null;
    notes: string | null;
  } | null> =>
    xhrService
      .get(
        `${assetsUrl(projectId, id)}/last-note`,
        excludeInspectionId
          ? { params: { exclude_inspection_id: excludeInspectionId } }
          : undefined,
      )
      .then((r) => r.data),

  addUpload: (
    projectId: number,
    id: number,
    data: {
      upload_id: number;
      inspection_id?: number | null;
      upload_type?: string;
    },
  ) =>
    xhrService
      .post(`${assetsUrl(projectId, id)}/uploads`, data)
      .then((r) => r.data),

  removeUpload: (projectId: number, id: number, uploadRowId: number) =>
    xhrService
      .delete(`${assetsUrl(projectId, id)}/uploads/${uploadRowId}`)
      .then((r) => r.data),

  linkFinding: (
    projectId: number,
    id: number,
    findingId: number,
  ): Promise<ProjectAsset> =>
    xhrService
      .post(`${assetsUrl(projectId, id)}/findings/${findingId}`)
      .then((r) => r.data),

  unlinkFinding: (
    projectId: number,
    id: number,
    findingId: number,
  ): Promise<ProjectAsset> =>
    xhrService
      .delete(`${assetsUrl(projectId, id)}/findings/${findingId}`)
      .then((r) => r.data),
};
